/**
 * Cognito Hosted UI authentication for the SPA.
 *
 * Flow:
 *   1. User clicks "Sign in" → redirect to `${hostedUi}/login?client_id=...
 *      &redirect_uri=...&response_type=code&scope=openid+email+profile`
 *   2. Cognito does the auth ceremony, redirects back to /auth/callback with
 *      `?code=<auth_code>`
 *   3. The SPA detects /auth/callback on load, POSTs to `${hostedUi}/oauth2/token`
 *      exchanging the code for tokens, stores id_token + refresh_token, and
 *      restores the pre-sign-in deep link (or "/")
 *   4. Every tRPC call sends `Authorization: Bearer <id_token>`; the client
 *      refreshes proactively when the token is near expiry (getValidToken)
 *      and reactively on UNAUTHORIZED (see trpc.ts authRetryLink)
 *
 * Token storage: localStorage (Phase 1a-UX-polish). XSS would expose both
 * tokens; the SPA has no third-party script execution surface, and an
 * httpOnly-cookie scheme needs a same-origin token endpoint we don't have
 * (the API is a Lambda Function URL on another origin). Revisit alongside
 * the custom auth screens (gallerywall-custom-auth-ui-pre-launch.md).
 *
 * Env vars (Vite sees them at build time):
 *   VITE_COGNITO_HOSTED_UI_URL  e.g. https://gallerywall-stg.auth.us-east-1.amazoncognito.com
 *   VITE_COGNITO_CLIENT_ID      e.g. 5tbbpn38o00hgoaqcba2902it0
 *   VITE_APP_DOMAIN             e.g. https://stg.gallerywall.photo
 */
const HOSTED_UI = import.meta.env.VITE_COGNITO_HOSTED_UI_URL ?? '';
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
const APP_DOMAIN =
  import.meta.env.VITE_APP_DOMAIN ?? (typeof window !== 'undefined' ? window.location.origin : '');

const TOKEN_STORAGE_KEY = 'gallerywall.id_token';
const REFRESH_TOKEN_STORAGE_KEY = 'gallerywall.refresh_token';
const POST_AUTH_PATH_KEY = 'gallerywall.post_auth_path';

/** How long before expiry we refresh proactively. */
const REFRESH_MARGIN_MS = 5 * 60 * 1000;
/** Saved deep links go stale fast — don't replay an abandoned one days later. */
const POST_AUTH_PATH_TTL_MS = 10 * 60 * 1000;

export function getToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

function setToken(token: string) {
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
}

function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY);
}

export function hasRefreshToken(): boolean {
  return getRefreshToken() !== null;
}

export function clearToken() {
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  window.localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);
}

/** Decode a JWT's exp claim (ms since epoch) without verifying — display/refresh timing only. */
function tokenExpiryMs(token: string): number | null {
  const part = token.split('.')[1];
  if (!part) return null;
  try {
    const payload = JSON.parse(atob(part.replace(/-/g, '+').replace(/_/g, '/'))) as {
      exp?: number;
    };
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Exchange the refresh token for a fresh id_token. Concurrent callers share
 * one in-flight request (uploads fire many parallel tRPC calls — only one
 * refresh should hit Cognito when they all notice expiry together).
 *
 * Returns true on success. On a definitive failure (Cognito rejected the
 * refresh token — expired after 30 days, or revoked) clears the session so
 * the caller can route to sign-in; on network errors leaves tokens alone.
 */
let refreshInFlight: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;
  const refreshToken = getRefreshToken();
  if (!refreshToken) return Promise.resolve(false);

  refreshInFlight = (async () => {
    try {
      const resp = await fetch(`${HOSTED_UI}/oauth2/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: CLIENT_ID,
          refresh_token: refreshToken,
        }),
      });
      if (!resp.ok) {
        // invalid_grant = refresh token expired/revoked → session is over.
        console.warn('token refresh rejected', resp.status, await resp.text());
        if (resp.status === 400 || resp.status === 401) clearToken();
        return false;
      }
      const data = (await resp.json()) as { id_token?: string; refresh_token?: string };
      if (!data.id_token) return false;
      setToken(data.id_token);
      // Cognito only returns a new refresh_token when rotation is enabled;
      // keep the current one otherwise.
      if (data.refresh_token) {
        window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);
      }
      return true;
    } catch (err) {
      console.warn('token refresh failed (network)', err);
      return false;
    } finally {
      refreshInFlight = null;
    }
  })();
  return refreshInFlight;
}

/**
 * The token to send on an API call, refreshing first if it's within
 * REFRESH_MARGIN_MS of expiry (or already expired). Returns whatever token is
 * stored if refresh isn't possible — the API's 401 + the reactive retry link
 * handle the rest.
 */
export async function getValidToken(): Promise<string | null> {
  const token = getToken();
  if (!token) return null;
  const exp = tokenExpiryMs(token);
  // exp === null means the stored token is corrupt (not decodable JWT) —
  // refresh it too. Without this, the API treats the bad Bearer as anonymous
  // and a private gallery 404s instead of 401ing, so the reactive retry link
  // never gets its signal (observed in e2e 2026-06-12).
  if ((exp === null || exp - Date.now() < REFRESH_MARGIN_MS) && hasRefreshToken()) {
    await refreshSession();
    return getToken();
  }
  return token;
}

/**
 * Remember where to land after the Cognito round-trip, with a freshness
 * timestamp — an abandoned sign-in must not teleport the user to a stale
 * deep link days later (observed 2026-06-12 replaying a dead share token).
 */
export function setPostAuthPath(path: string) {
  window.localStorage.setItem(POST_AUTH_PATH_KEY, JSON.stringify({ path, ts: Date.now() }));
}

function consumePostAuthPath(): string {
  const raw = window.localStorage.getItem(POST_AUTH_PATH_KEY);
  window.localStorage.removeItem(POST_AUTH_PATH_KEY);
  if (!raw) return '/';
  try {
    const { path, ts } = JSON.parse(raw) as { path?: string; ts?: number };
    if (typeof ts !== 'number' || Date.now() - ts > POST_AUTH_PATH_TTL_MS) return '/';
    // Same-origin relative paths only — anything else falls back to "/" so a
    // poisoned localStorage value can't turn the callback into an open redirect.
    return path && path.startsWith('/') && !path.startsWith('//') ? path : '/';
  } catch {
    return '/';
  }
}

/** URL to redirect to for sign-in. */
export function signInUrl(): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    response_type: 'code',
    scope: 'openid email profile',
    redirect_uri: `${APP_DOMAIN}/auth/callback`,
  });
  return `${HOSTED_UI}/login?${params}`;
}

/** URL to redirect to for sign-out — Cognito clears its own session too. */
export function signOutUrl(): string {
  const params = new URLSearchParams({
    client_id: CLIENT_ID,
    logout_uri: `${APP_DOMAIN}/auth/logout`,
  });
  return `${HOSTED_UI}/logout?${params}`;
}

/**
 * Full local sign-out: best-effort revoke of the refresh token (so a copied
 * token can't outlive the session), then clear storage. Caller then redirects
 * to signOutUrl() to end the Cognito-side session.
 */
export async function signOut(): Promise<void> {
  const refreshToken = getRefreshToken();
  if (refreshToken) {
    try {
      await fetch(`${HOSTED_UI}/oauth2/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ client_id: CLIENT_ID, token: refreshToken }),
      });
    } catch (err) {
      console.warn('refresh token revoke failed', err);
    }
  }
  clearToken();
}

/**
 * Called once on app load. If the URL is the Cognito callback (`/auth/callback`
 * with `?code=...`), exchange the code for tokens, store them, and replace the
 * URL with the saved deep link (or `/`) so a refresh doesn't try to re-exchange
 * a one-shot code. Returns true if a callback was handled.
 */
export async function handleAuthCallback(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname !== '/auth/callback') return false;

  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) {
    // Could be an error from Cognito (?error=access_denied&...); surface
    // succinctly and bounce to root so the user can retry.
    console.warn('callback hit without code', params.toString());
    window.history.replaceState(null, '', '/');
    return true;
  }

  const tokenResp = await fetch(`${HOSTED_UI}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: CLIENT_ID,
      code,
      redirect_uri: `${APP_DOMAIN}/auth/callback`,
    }),
  });
  if (!tokenResp.ok) {
    const text = await tokenResp.text();
    console.error('token exchange failed', tokenResp.status, text);
    window.history.replaceState(null, '', '/');
    return true;
  }
  const data = (await tokenResp.json()) as { id_token?: string; refresh_token?: string };
  if (data.id_token) {
    setToken(data.id_token);
  }
  if (data.refresh_token) {
    window.localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, data.refresh_token);
  }
  window.history.replaceState(null, '', consumePostAuthPath());
  return true;
}
